import axios from "axios";
import { message } from 'antd';

const BASE_URL = process.defineEnv.API_BASE_URL;
const API_TIME_OUT = 30000;
let pendingRequests = [];

export const AUTH_EXPIRED_CODE = 401;
export const AUTH_EXPIRED_EVENT = "logan-auth-expired";

// axios instance
const instance = axios.create({
  baseURL: BASE_URL,
  timeout: API_TIME_OUT,
  withCredentials: true
});

const REQUEST_TIMEOUT_ERROR_KEY = "logan-request-timeout-error";

function showRequestError(content, key) {
  message.open({
    type: "error",
    content,
    key,
    duration: 3
  });
}


// Native Apis

// Auth Apis

export function fetchAuthSessionApi() {
  return instance.get("/auth/session.json", {
    headers: {
      publicRequest: true
    }
  });
}

export function loginApi(username, password) {
  return instance.post("/auth/login.json", {
    username,
    password
  }, {
    headers: {
      publicRequest: true
    }
  });
}

export function logoutApi() {
  return instance.post("/auth/logout.json");
}

export function fetchUsersApi() {
  return instance.get("/auth/users/list.json");
}

export function createUserApi(user) {
  return instance.post("/auth/users/create.json", user);
}

export function updateUserPasswordApi(id, password) {
  return instance.post(`/auth/users/${id}/password.json`, {
    password
  });
}

export function updateUserStatusApi(id, enabled) {
  return instance.post(`/auth/users/${id}/status.json`, {
    enabled
  });
}

export function updateUserRoleApi(id, role) {
  return instance.post(`/auth/users/${id}/role.json`, {
    role
  });
}

export function fetchNativeListInitData() {
  return instance.get("/logan/latest.json");
}

// 服务端分页，返回 {list, total, pageNum, pageSize}
export function fetchNativeTaskApi(deviceId, platform, beginTime, endTime, appId, appVersion, diagnosisId, uploadType, pageNum, pageSize, descriptionKeyword) {
  return instance.get("/logan/task/search.json", {
    params: {
      deviceId,
      platform,
      beginTime,
      endTime,
      appId,
      appVersion,
      diagnosisId,
      uploadType,
      descriptionKeyword: descriptionKeyword || void 0,
      pageNum,
      pageSize
    }
  });
}

export function fetchNativeTaskInfoByTaskIdApi(taskId) {
  return instance.get(`/logan/task/${taskId}/info.json`);
}

export function fetchNativeLogTypesApi() {
  return instance.get("/logan/meta/logtypes.json");
}

export function fetchNativeTaskBriefsByTaskIdApi(taskId, logTypes, keyword) {
  return instance.get(`/logan/task/${taskId}/brief.json`, {
    params: {
      logTypes,
      keyword
    }
  });
}

export function fetchNativeTaskDetailsByDetailIdsApi(detailIds) {
  if (!detailIds || (Array.isArray(detailIds) && detailIds.length === 0) || detailIds === "") {
    return Promise.resolve([]);
  }
  return instance.get("/logan/task/query/details.json", {
    params: {
      detailIds: detailIds
    }
  });
}

export function fetchNativeSingleLogDetailByLogIdApi(logId) {
  return instance.get(`/logan/task/${logId}/detail.json`);
}

export function fetchNativeDownloadUrlApi(taskId) {
  return instance.get(`/logan/task/${taskId}/download.json`);
}


// Web apis


export function fetchWebDownloadUrlApi(tasks) {
  return instance.get("/logan/web/getDownLoadUrl.json", {
    params: {
      tasks: tasks
    }
  })
}

export function fetchWebListInitData() {
  return instance.get("/logan/web/latest.json");
}

export function fetchWebTaskApi(deviceId, beginTime, endTime) {
  return instance.get("/logan/web/search.json", {
    params: {
      deviceId,
      beginTime,
      endTime
    }
  })
}

export function fetchWebBriefsApi(taskId, logTypes, keyword) {
  return instance.get("/logan/web/detailIndex.json", {
    params: {
      tasks: taskId,
      logTypes: logTypes,
      keyword: keyword,
      beginTime: null,
      endTime: null
    }
  })
}

export function fetchWebTaskDetailsByDetailIdsApi(detailIds) {
  if (!detailIds || (Array.isArray(detailIds) && detailIds.length === 0) || detailIds === "") {
    return Promise.resolve([]);
  }
  return instance.get("/logan/web/details.json", {
    params: {
      detailIds
    }
  })
}

export function fetchWebTaskInfoByTaskIdApi(taskId) {
  return instance.get("/logan/web/taskDetail.json", {
    params: {
      tasks: taskId
    }
  })
}

export function fetchWebSingleLogDetailByLogIdApi(detailId) {
  return instance.get("/logan/web/logDetail.json", {
    params: {
      detailId
    }
  })
}


// private request configs

const RemovePendingRequest = (config) => {
  pendingRequests.forEach((request, idx) => {
    if (request.id === config.url + '&' + config.method) {
      request.cancel();
      pendingRequests.splice(idx, 1);
    }
  });
};

const AddPendingRequest = (config) => {
  return new axios.CancelToken(c => {
    pendingRequests.push({
      id: config.url + '&' + config.method,
      cancel: c
    });
  });
};

instance.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  if (config.headers.cancelOthers) {
    delete config.headers.cancelOthers;
    RemovePendingRequest(config);
    config.cancelToken = AddPendingRequest(config);
  }
  if (config.headers.removeXRequestedWith) {
    delete config.headers.removeXRequestedWith;
    delete config.headers['X-Requested-With'];
  }
  if (config.headers.publicRequest) {
    config.skipAuthExpiredHandling = true;
    delete config.headers.publicRequest;
  }
  return config;
});

function onRequestFulfilled(response) {
  RemovePendingRequest(response.config);
  let {code, msg, data} = response.data;
  if (Number(code) !== 200) {
    if (Number(code) !== AUTH_EXPIRED_CODE) {
      message.warning({
        content: `请求出错：${msg}`,
        key: `logan-request-warning-${code}-${msg}`
      });
    } else if (!response.config.skipAuthExpiredHandling) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    return Promise.reject(response.data);
  }
  return data;
}

function onRequestRejected(error) {
  if (!error.request) {
    return null;
  }
  let status = error.response && error.response.status;
  if (Number(status) !== AUTH_EXPIRED_CODE) {
    const isTimeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
    if (isTimeout) {
      showRequestError("请求超时，请稍后重试", REQUEST_TIMEOUT_ERROR_KEY);
    } else {
      message.error(`请求出错${status || error.message}`);
    }
  } else if (!error.config || !error.config.skipAuthExpiredHandling) {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
  return Promise.reject(error);
}

instance.interceptors.response.use(onRequestFulfilled, onRequestRejected);
